import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CheckCircle,
  Clock,
  Building,
  RefreshCw,
  Search,
  Eye,
  Loader2,
  XCircle,
  Info,
  Users
} from 'lucide-react';
import { summarizationService } from '../services/summarizationService';

const SummarizationDashboard = () => {
  const navigate = useNavigate();
  const [summarizations, setSummarizations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    company: '',
    status: '',
    quarter: '',
    page: 1,
    limit: 10
  });
  const [companySearchInput, setCompanySearchInput] = useState('');
  const [highlightsModal, setHighlightsModal] = useState({ show: false, data: null, loading: false });

  // Load dashboard data
  const loadDashboard = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const params = {};
      if (filters.company) params.company_filter = filters.company;
      if (filters.status) params.status_filter = filters.status;
      if (filters.quarter) params.quarter_filter = filters.quarter;
      params.page = filters.page;
      params.limit = filters.limit;

      const [dashboardData, statsData] = await Promise.all([
        summarizationService.getDashboardSummarizations(params),
        summarizationService.getDashboardStats()
      ]);

      setSummarizations(dashboardData.items || []);
      setPagination(dashboardData.pagination || {});
      setStats(statsData);

    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filters]);

  // Load active jobs for polling
  const loadActiveJobs = useCallback(async () => {
    try {
      const activeData = await summarizationService.getActiveJobs();
      // Update active job statuses in the current summarizations list
      if (activeData.active_jobs && activeData.active_jobs.length > 0) {
        setSummarizations(prev => prev.map(summary => {
          const activeJob = activeData.active_jobs.find(job => job.job_id === summary.job_id);
          return activeJob ? { ...summary, ...activeJob } : summary;
        }));
      }
    } catch (err) {
      // Silently handle polling errors
      console.warn('Failed to load active jobs:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Polling for active jobs
  useEffect(() => {
    const interval = setInterval(loadActiveJobs, 30000); // Poll every 30 seconds (reduced from 5s)
    return () => clearInterval(interval);
  }, [loadActiveJobs]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  // Handle company search on Enter key
  const handleCompanySearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFilterChange('company', companySearchInput);
    }
  };

  // Handle company search input change
  const handleCompanyInputChange = (value) => {
    setCompanySearchInput(value);
    // Clear the filter if input is empty
    if (value === '') {
      handleFilterChange('company', '');
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { class: 'status-badge success', icon: CheckCircle },
      partial: { class: 'status-badge warning', icon: Clock }
    };

    const config = statusConfig[status] || statusConfig.partial;
    const IconComponent = config.icon;

    return (
      <span className={config.class}>
        <IconComponent style={{ width: '12px', height: '12px' }} />
        {status}
      </span>
    );
  };

  // Handle view summary - navigate to full summary page
  const handleViewSummary = (jobId, status) => {
    // Navigate to the SummaryViewer page with the job ID
    navigate(`/summary/${jobId}`);
  };

  // Handle view call highlights - generate insights from transcription data
  const handleViewHighlights = async (jobId, status) => {
    try {
      setHighlightsModal({ show: true, data: null, loading: true });
      
      // For highlights, we always generate fresh insights from transcription data
      // This ensures we get the most accurate speaker diarization and insights
      const response = await summarizationService.getCallInsights(jobId, 'gemini');
      
      setHighlightsModal({ 
        show: true, 
        data: response, 
        loading: false 
      });
    } catch (error) {
      console.error('Error generating call insights:', error);
      setHighlightsModal({ 
        show: true, 
        data: { error: error.message || 'Failed to generate call insights' }, 
        loading: false 
      });
    }
  };

  // Close modals
  const closeHighlightsModal = () => {
    setHighlightsModal({ show: false, data: null, loading: false });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-container">
          <Loader2 style={{ width: '48px', height: '48px', animation: 'spin 1s linear infinite', color: '#3b82f6', marginBottom: '16px' }} />
          <p>Loading summarization dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div className="container" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
        {/* Header */}
        <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="dashboard-title">Summarization Dashboard</h1>
            <p className="dashboard-subtitle">Monitor AI summarization jobs, view insights, and manage results</p>
          </div>
          <button
            onClick={() => loadDashboard()}
            disabled={loading}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Refresh
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-container">
            <div className="error-title">Error</div>
            <div className="error-message">{error}</div>
          </div>
        )}

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-4" style={{ marginBottom: '32px' }}>
            <div className="stats-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BarChart3 style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <div className="stats-label">Total Summarizations</div>
                  <div className="stats-number">
                    {stats.summarizations?.total || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <div className="stats-label">Completed</div>
                  <div className="stats-number">
                    {stats.summarizations?.completed || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Clock style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <div className="stats-label">Partial</div>
                  <div className="stats-number">
                    {stats.summarizations?.partial || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#8b5cf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <div className="stats-label">Companies</div>
                  <div className="stats-number">
                    {stats.companies_count || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="form-section" style={{ marginBottom: '32px' }}>
          <h2 className="form-title">Filters & Search</h2>
          <div className="filter-grid">
            <div className="form-group">
              <label className="label">
                <Search style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                Company Name
              </label>
              <input
                type="text"
                value={companySearchInput}
                onChange={(e) => handleCompanyInputChange(e.target.value)}
                onKeyPress={handleCompanySearchKeyPress}
                placeholder="Search by company name... (Press Enter to search)"
                className="input"
              />
            </div>

            <div className="form-group">
              <label className="label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="select"
              >
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="partial">Partial</option>
              </select>
            </div>

            <div className="form-group">
              <label className="label">Quarter</label>
              <select
                value={filters.quarter}
                onChange={(e) => handleFilterChange('quarter', e.target.value)}
                className="select"
              >
                <option value="">All Quarters</option>
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summarizations Table */}
        <div className="table-container">
          <div className="table-header">
            <h3 className="table-title">Summarization Jobs</h3>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              {pagination.total_items || 0} total summarizations
            </div>
          </div>

          {summarizations.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              color: '#64748b',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <BarChart3 style={{ width: '48px', height: '48px', color: '#cbd5e1' }} />
              <div>
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>No summarizations found</div>
                <div style={{ fontSize: '14px' }}>Try adjusting your filters or refresh the page.</div>
              </div>
            </div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Call ID</th>
                    <th>Call Type</th>
                    <th>Quarter/Date</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {summarizations.map((summarization) => (
                    <tr key={summarization.job_id}>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500', color: '#1e293b' }}>
                            {summarization.company_name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {summarization.job_id}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#1e293b' }}>
                          {summarization.call_id || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '14px', color: '#1e293b' }}>
                          {summarization.call_type || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500' }}>{summarization.quarter}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {summarization.date}
                          </div>
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(summarization.status)}
                      </td>
                      <td>
                        <div style={{ fontSize: '14px' }}>
                          {formatDate(summarization.created_at)}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleViewSummary(summarization.job_id, summarization.status)}
                            className="btn btn-secondary"
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '12px',
                              minWidth: 'auto'
                            }}
                            title="View Full Summary"
                          >
                            <Eye style={{ width: '14px', height: '14px' }} />
                            Summary
                          </button>
                          <button
                            onClick={() => handleViewHighlights(summarization.job_id, summarization.status)}
                            className="btn btn-primary"
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '12px',
                              minWidth: 'auto'
                            }}
                            title="View Call Highlights"
                          >
                            <Users style={{ width: '14px', height: '14px' }} />
                            Highlights
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div style={{
                  padding: '16px 24px',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Showing {((pagination.current_page || 1) - 1) * (pagination.items_per_page || 10) + 1} to{' '}
                    {Math.min((pagination.current_page || 1) * (pagination.items_per_page || 10), pagination.total_items || 0)} of{' '}
                    {pagination.total_items || 0} results
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page <= 1}
                      className="btn btn-secondary"
                      style={{ fontSize: '14px', padding: '8px 12px' }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page >= pagination.total_pages}
                      className="btn btn-secondary"
                      style={{ fontSize: '14px', padding: '8px 12px' }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Information Box */}
        <div className="info-box">
          <div className="info-title">
            <Info style={{ width: '16px', height: '16px' }} />
            Dashboard Information
          </div>
          <ul className="info-list">
            <li><strong>Real-time Updates:</strong> Dashboard automatically refreshes active job statuses every 5 seconds</li>
            <li><strong>View Summary:</strong> Click "Summary" to see the complete AI-generated analysis</li>
            <li><strong>Call Highlights:</strong> Click "Highlights" to see speaker insights and key moments</li>
            <li><strong>Company Search:</strong> Enter company name and press Enter to search</li>
            <li><strong>Filtering:</strong> Use filters to find specific summarizations by company, status, or quarter</li>
          </ul>
        </div>
      </div>

      {/* Call Highlights Modal */}
      {highlightsModal.show && (
        <div className="modal-overlay" onClick={closeHighlightsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h3>Call Insights</h3>
              <button 
                onClick={closeHighlightsModal}
                className="modal-close-btn"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {highlightsModal.loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 className="spin" style={{ width: '32px', height: '32px' }} />
                  <p>Generating call insights...</p>
                </div>
              ) : highlightsModal.data?.error ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
                  <p>Error: {highlightsModal.data.error}</p>
                </div>
              ) : (
                <div className="call-insights-content">
                  {/* Header with call summary */}
                  {highlightsModal.data?.insights?.metadata && (
                    <div style={{
                      padding: '16px 20px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '12px',
                      marginBottom: '24px',
                      color: 'white'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                            Company: {highlightsModal.data.insights.metadata.company_name} | Quarter: {highlightsModal.data.insights.metadata.quarter} | Date: {highlightsModal.data.insights.metadata.date?.replace(/_/g, '_')}
                          </div>
                          <div style={{ fontSize: '14px', opacity: 0.9 }}>
                            {highlightsModal.data.insights.highlights?.length || 0} highlights • {Math.round((highlightsModal.data.insights.metadata.total_duration || 0) / 60)}m
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Key Highlights Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '16px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      <Info style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
                      Key Highlights ({highlightsModal.data?.insights?.highlights?.length || 0})
                    </div>

                    {highlightsModal.data?.insights?.highlights?.map((highlight, index) => {
                      // Category color mapping
                      const getCategoryColor = (category) => {
                        const colors = {
                          'Financial Performance': '#10b981',
                          'Business Strategy': '#3b82f6', 
                          'Future Outlook': '#8b5cf6',
                          'Risk Factors': '#ef4444',
                          'Operational Updates': '#f59e0b',
                          'Market Dynamics': '#06b6d4'
                        };
                        return colors[category] || '#6b7280';
                      };

                      return (
                        <div key={index} style={{
                          marginBottom: '16px',
                          padding: '16px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          backgroundColor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '12px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#374151'
                              }}>
                                {highlight.speaker_name?.charAt(0) || 'S'}
                              </div>
                              <div>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#1f2937',
                                  marginBottom: '2px'
                                }}>
                                  {highlight.speaker_name || 'Speaker'}
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#6b7280'
                                }}>
                                  {highlight.speaker_role || 'Executive'} • {highlight.timestamp_estimate || 'Unknown time'}
                                </div>
                              </div>
                            </div>
                            <span style={{
                              fontSize: '11px',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              backgroundColor: getCategoryColor(highlight.category) + '20',
                              color: getCategoryColor(highlight.category),
                              fontWeight: '600',
                              border: `1px solid ${getCategoryColor(highlight.category)}40`
                            }}>
                              {highlight.category || 'General'}
                            </span>
                          </div>
                          
                          <div style={{
                            fontSize: '14px',
                            lineHeight: '1.6',
                            color: '#374151',
                            padding: '12px 16px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            borderLeft: `4px solid ${getCategoryColor(highlight.category)}`,
                            marginBottom: '8px'
                          }}>
                            "{highlight.insight || highlight.quote || 'Key insight from the speaker'}"
                          </div>

                          {highlight.context && (
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              fontStyle: 'italic',
                              marginTop: '8px'
                            }}>
                              Context: {highlight.context}
                            </div>
                          )}

                          {highlight.topics && highlight.topics.length > 0 && (
                            <div style={{
                              marginTop: '8px',
                              display: 'flex',
                              gap: '6px',
                              flexWrap: 'wrap'
                            }}>
                              {highlight.topics.map((topic, topicIndex) => (
                                <span key={topicIndex} style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  backgroundColor: '#e5e7eb',
                                  color: '#374151'
                                }}>
                                  {topic}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }) || (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        <Info style={{ width: '32px', height: '32px', marginBottom: '12px' }} />
                        <p>No call highlights available. Try generating insights from transcription data.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummarizationDashboard;
